<template>
  <div class="ce-rangeFilter">
    <div
      class="ce-rangeFilter-headline"
      v-html="label" />

    <vue-slider
      v-model="internalValue"
      :lazy="true"
      :min="min"
      :max="max"
      tooltip="always"
      tooltip-placement="bottom"
      :tooltip-formatter="tooltipFormatter"
      :clickable="true"
      :data="sliderData" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VueSlider from 'vue-slider-component';

import { getRange } from 'utils';

export default {
  props: {
    options: {
      type: Object,
      required: false,
      default: () => {}
    },
    label: {
      type: String,
      required: true
    },
    filterKey: {
      type: String,
      required: true
    },
    filterCode: {
      type: String,
      required: true
    },
    value: {
      // eslint-disable-next-line no-bitwise
      type: Number | Array,
      required: false,
      default: 0
    }
  },
  components: {
    VueSlider
  },
  computed: {
    ...mapGetters(['aggregations', 'urlFilters']),
    min() {
      if (this.valueAggs) return this.valueAggs.min;

      return this.options.min ? this.options.min : 0;
    },
    max() {
      if (this.valueAggs) return this.valueAggs.max;

      return this.options.max ? this.options.max : 100;
    },
    valueAggs() {
      if (this.aggregations) {
        let aggs = null;
        if (this.aggregations.hasOwnProperty(this.filterKey)) aggs = this.aggregations[this.filterKey];
        else if (this.aggregations.hasOwnProperty(this.filterCode)) aggs = this.aggregations[this.filterCode];

        if (aggs && typeof aggs === 'object' && aggs.hasOwnProperty('min') && aggs.hasOwnProperty('max')) {
          return {
            min: aggs.min.value,
            max: aggs.max.value
          };
        }
      }

      return null;
    },
    sliderData() {
      return [0, ...getRange(this.min, this.max)];
    },
    anyLabel() {
      return this.options && this.options.anyLabel ? this.options.anyLabel : 'any';
    },
    internalValue: {
      get() {
        if (this.options.range) {
          return Array.isArray(this.value) ? [parseInt(this.value[0]), parseInt(this.value[1])] : [this.min, this.max];
        }

        return this.value !== null ? parseInt(this.value) : 0;
      },
      set(val) {
        const url = this.$cloneObject(this.urlFilters);
        url[this.filterKey] = val;
        this.$store.dispatch('setUrlFilters', url);
        this.$trackEvent('filter', 'toggle', {
          filterType: 'range',
          filterLabel: this.label,
          filterKey: this.filterKey,
          value: val
        });
      }
    }
  },
  methods: {
    tooltipFormatter(value) {
      if (this.options && this.options.unit) {
        return (value !== 0 || this.options.range) ? `${value}${this.options.unit}` : this.anyLabel;
      }

      return value;
    }
  }
};
</script>

<style lang="less">
  @import (reference) "~styles/includes/vars";
  @sliderMainColor: @ce-slider-filter-main-color;

  .ce-rangeFilter {
    padding: @space-3 0 @space-5;

    .vue-slider {
      padding: @space-2;
    }

    .ce-rangeFilter-headline {
      margin-bottom: @space-2;
      font-weight: 600;
    }

    .vue-slider-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .vue-slider-rail {
      background-color: @color-mono-92;
      border-radius: 15px;
    }

    .vue-slider-process {
      background-color: @sliderMainColor;
      border-radius: 15px;
    }

    .vue-slider-mark {
      z-index: 4;

      &:first-child .vue-slider-mark-step, &:last-child .vue-slider-mark-step {
        display: none;
      }
    }

    .vue-slider-mark-step {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: rgba(0, 0, 0, 0.16);
    }
    .vue-slider-mark-label {
      font-size: 14px;
      white-space: nowrap;
    }
    .vue-slider-dot-handle {
      cursor: pointer;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: @sliderMainColor;
      box-sizing: border-box;
      box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
    }
    .vue-slider-dot-handle-focus {
      box-shadow: 0 0 1px 2px rgba(52, 152, 219, 0.36);
    }

    .vue-slider-dot-handle-disabled {
      cursor: not-allowed;
      background-color: @color-mono-40;
    }

    .vue-slider-dot-tooltip-inner {
      font-size: 14px;
      white-space: nowrap;
      padding: 2px 5px;
      min-width: 20px;
      text-align: center;
      color: #fff;
      border-radius: 5px;
      border-color: @sliderMainColor;
      background-color: @sliderMainColor;
      box-sizing: content-box;
      font-weight: 600;

      &::after {
        content: "";
        position: absolute;
      }
    }

    .vue-slider-dot-tooltip-inner-top::after {
      top: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-top-color: inherit;
    }
    .vue-slider-dot-tooltip-inner-bottom::after {
      bottom: 100%;
      left: 50%;
      transform: translate(-50%, 0);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-bottom-color: inherit;
    }
    .vue-slider-dot-tooltip-inner-left::after {
      left: 100%;
      top: 50%;
      transform: translate(0, -50%);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-left-color: inherit;
    }
    .vue-slider-dot-tooltip-inner-right::after {
      right: 100%;
      top: 50%;
      transform: translate(0, -50%);
      height: 0;
      width: 0;
      border-color: transparent;
      border-style: solid;
      border-width: 5px;
      border-right-color: inherit;
    }

    .vue-slider-dot-tooltip-wrapper {
      opacity: 0;
      transition: all 0.3s;
    }
    .vue-slider-dot-tooltip-wrapper-show {
      opacity: 1;
    }
  }
</style>
